import { LoadingButton } from '@mui/lab'
import { Box, Button, Card, Grid, TextField, Typography, useMediaQuery } from '@mui/material'
import { AxiosError } from 'axios'
import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { hasProvidedDocument } from '../../../domain/Adopters/adoptionDocuments'
import { getGuestCheckout } from '../../../domain/Adopters/GuestAdoption'
import { AdoptersClient } from '../../../interactors/clients/AdoptersClient'
import { AnimalsClient } from '../../../interactors/clients/AnimalsClient'
import { CustomersClient } from '../../../interactors/clients/CustomerClient'
import {
  AdoptionAttemptWithGuestCheckoutDto,
  GuestAdoptionLinkDto,
} from '../../../interactors/clients/GuestAdoptionsClient'
import {
  AccountDtoPermission,
  AdopterDto,
  AdoptionAttemptDto,
  AdoptionAttemptDtoStatus,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDto,
  CustomerDto,
} from '../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../store/AccountsStore'
import { useAdoptersStore } from '../../../store/AdoptersStore'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { useFetchOrRefreshAdoptersOnMount } from '../../../store/useFetchOrRefreshAdoptersOnMount'
import { useFetchOrRefreshMembersOnMount } from '../../../store/useFetchOrRefreshMembersOnMount'
import {
  isDoneOutsidePetsoAttempt,
  isPetsoAttemptWithManualDocuments,
  usesManualDocumentFlow,
} from '../../../utils/adoptionAttemptOrigin'
import { readableAxiosError } from '../../../utils/axios'
import { getAdoptionAttemptStatus } from '../../../utils/getAdoptionAttemptStatus'
import { CollapsableCard } from '../../common/CollapsableCard'
import { addPrimaryFabSx, addSecondaryFabSx } from '../../common/addPrimaryCtaSx'
import { spacingItem, theme } from '../../theme'
import { AllAdoptionInsightsInfoBox } from './AllAdoptionInsightsInfoBox'
import { ContributionCard } from './ContributionCard'
import { AdoptionAttemptEvents } from './Events'
import { ExitDatesCard } from './ExitDatesCard'
import { ExitDocumentCard } from './ExitDocumentCard'
import { ExitDocumentsCard } from './ExitDocumentsCard'
import { FileCard } from './FileCard'
import { GuestAdoptionCard } from '../GuestAdoption/GuestAdoptionCard'
import { OnGoingAdoptionAnimalCard } from './OnGoingAdoptionAnimalCard'
import { RestartProcedureModal } from './RestartProcedureModal'
import { SuspendProcedureModal } from './SuspendProcedureModal'
import { UnlockContractModal } from './UnlockContractModal'
import { SterilizationCard } from './SterilizationCard'
import { EndAdoptionPopUp } from '../AdopterPastAdoptions/EndAdoptionPopUp'
import { useCanEditAnimal } from '../../../store/useCanEditAnimal'
import { useAnimalStore } from '../../../store/AnimalStore'

interface Props {
  adopter: AdopterDto
}

export const OnGoingAdoptionScreen: React.FC<Props> = ({ adopter }) => {
  const queryParams = new URLSearchParams(window.location.search)
  const navigate = useNavigate()
  const location = useLocation()
  const globalSnackBarStore = useGlobalSnackbarStore()
  const animalStore = useAnimalStore()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [adoptionAttempt, setAdoptionAttempt] = useState<AdoptionAttemptDto | undefined>(undefined)
  const [animal, setAnimal] = useState<AnimalDto | undefined>(undefined)
  const [association, setAssociation] = useState<CustomerDto | null>(null)
  const animalId = adoptionAttempt?.animalId
  const adoptersStore = useAdoptersStore()
  const [restartModalOpen, setRestartModalOpen] = useState(false)
  const [suspendModalOpen, setSuspendModalOpen] = useState(false)
  const [unlockModalOpen, setUnlockModalOpen] = useState(false)
  const [endModalOpen, setEndModalOpen] = useState(false)

  const onCloseUnlockModal = () => setUnlockModalOpen(false)
  const onCloseRestartModal = () => setRestartModalOpen(false)
  const onCloseSuspendModal = () => setSuspendModalOpen(false)

  useFetchOrRefreshMembersOnMount()
  useFetchOrRefreshAdoptersOnMount()

  useEffect(() => {
    if (queryParams.get('attemptId') != null) {
      const attempt = adopter.adoptionAttempts?.find(
        (adoptionAttempt) => adoptionAttempt.id === queryParams.get('attemptId')
      )
      setAdoptionAttempt(attempt)
    }
  }, [adopter.adoptionAttempts])

  useEffect(() => {
    if (queryParams.get('endModal') != null) {
      setEndModalOpen(Boolean(queryParams.get('endModal')))
    }
  }, [location.search])

  useEffect(() => {
    const fetchAnimal = async () => {
      if (animalId == null) return
      const animal = await AnimalsClient.getAnimal(animalId)
      if (animal) {
        setAnimal(animal)
        animalStore.changeSelectedAnimal(animal.id)
      }
    }
    const loadAssociation = async () => {
      const selectedAssociation = await CustomersClient.getCurrentCustomer()
      setAssociation(selectedAssociation || null)
    }

    loadAssociation()
    fetchAnimal()
  }, [animalId])

  const updateAttemptMutation = useMutation(
    async (data: AdoptionAttemptDto) => {
      const response: AdoptionAttemptDto = await AdoptersClient.updateAdoptionAttempt(adopter.id, data)
      return response
    },
    {
      onSuccess: (attempt) => {
        if (
          adoptionAttempt?.contract?.isLocked &&
          !adoptionAttempt?.certificate?.validated &&
          attempt.certificate?.validated &&
          attempt.contract?.original &&
          attempt.contract?.original !== ''
        ) {
          setUnlockModalOpen(true)
        }
        setAdoptionAttempt(attempt)
        adopter.adoptionAttempts = adopter.adoptionAttempts?.map((a) => (a.id === attempt.id ? attempt : a))
        globalSnackBarStore.triggerSuccessMessage(`Vos modifications ont bien été enregistrées.`)
      },
      onError: (error: Error | AxiosError) => {
        globalSnackBarStore.triggerErrorMessage(readableAxiosError(error).join(' '))
      },
    }
  )

  const deleteAttempt = () => {
    if (adoptionAttempt == null) return
    adopter.adoptionAttempts = adopter.adoptionAttempts?.filter((attempt) => attempt.id !== adoptionAttempt.id)
    AdoptersClient.editAccount(adopter)
    globalSnackBarStore.triggerSuccessMessage(`La procédure d'adoption a bien été supprimée.`)
    navigate(`/suivi-adoptant/${adopter.id}?tab=profil`)
  }

  const onClickUnlockContract = async () => {
    if (adoptionAttempt && adoptionAttempt.contract) {
      const newAttempt = {
        ...adoptionAttempt,
        contract: {
          ...adoptionAttempt.contract,
          isLocked: false,
        },
      }
      setAdoptionAttempt(newAttempt)
      await updateAttemptMutation.mutateAsync(newAttempt)
      setUnlockModalOpen(false)
    }
  }

  const onClickEnd = async (withAnimal = false) => {
    if (withAnimal) return setEndModalOpen(true)
    if (adoptionAttempt && animal) {
      await updateAttemptMutation.mutateAsync({
        ...adoptionAttempt,
        done: true,
        archived: true,
      })
      if (withAnimal) {
        animal.adoption.status = AnimalAdoptionDtoStatus.HasBeenAdopted
        animal.adoption.adopterId = adopter.id
        animal.adoption.step = AnimalAdoptionDtoStep.AdoptionValidatedWithoutPostVisit
        await AnimalsClient.editAnimal(animal)
      }
      return navigate(`/suivi-adoptant/${adopter.id}?tab=past-adoption&attemptId=${adoptionAttempt.id}`)
    }
  }

  const setAnimalAdoptionStatusToAdoptable = () => {
    if (animal && animal.adoption.status !== AnimalAdoptionDtoStatus.Adoptable) {
      animal.adoption.status = AnimalAdoptionDtoStatus.Adoptable
      animal.adoption.adopterId = ''
      AnimalsClient.editAnimal(animal)
    }
  }

  const onClickSuspendAttempt = async () => {
    if (!adoptionAttempt) return
    const newAttempt = {
      ...adoptionAttempt,
      status: AdoptionAttemptDtoStatus.SuspendedByCustomer,
    }
    setAdoptionAttempt(newAttempt)
    setAnimalAdoptionStatusToAdoptable()
    await updateAttemptMutation.mutateAsync(newAttempt)
    setSuspendModalOpen(false)
    navigate(`/suivi-adoptant/${adopter.id}?tab=suspended-adoption&attemptId=${adoptionAttempt.id}`)
  }

  const onClickRestartAttempt = async (confirmation?: boolean) => {
    if (!adoptionAttempt || !animal) return

    if (animal.adoption.adopterId && animal.adoption.adopterId !== adopter.id && !confirmation) {
      return setRestartModalOpen(true)
    }
    if (confirmation) {
      const oldAdopter = adoptersStore.adopters.find((a) => a.id === animal.adoption.adopterId)
      if (oldAdopter) {
        const attempt = oldAdopter.adoptionAttempts?.find(
          (a) => a.animalId === adoptionAttempt.animalId && a.status !== AdoptionAttemptDtoStatus.SuspendedByCustomer
        )
        if (attempt) {
          attempt.status = AdoptionAttemptDtoStatus.SuspendedByCustomer
          await AdoptersClient.updateAdoptionAttempt(oldAdopter.id, attempt)
        }
      }
    }
    await updateAttemptMutation.mutateAsync({
      ...adoptionAttempt,
      id: adoptionAttempt.id,
      animalId: adoptionAttempt.animalId,
      status: AdoptionAttemptDtoStatus.PendingUser,
      step: adoptionAttempt.step,
      done: false,
      customerName: adoptionAttempt.customerName,
    })
    animal.adoption.status = AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable
    animal.adoption.adopterId = adopter.id
    animal.adoption.step = undefined
    animal.adoption.vpaDate = undefined
    animal.adoption.exitDate = undefined
    animal.adoption.adoptionDate = undefined
    await AnimalsClient.editAnimal(animal)
    navigate(`/suivi-adoptant/${adopter.id}?tab=ongoing-adoption&attemptId=${adoptionAttempt.id}`)
  }

  const accountsStore = useAccountsStore()
  const isReader = accountsStore.connectedAccount?.permission === AccountDtoPermission.Read
  const CanEditAnimal = useCanEditAnimal()
  const isPastAdoptionOutsidePetso = isDoneOutsidePetsoAttempt(adoptionAttempt)
  const usesManualDocuments = usesManualDocumentFlow(adoptionAttempt)
  const isPetsoManualDocumentFlow = isPetsoAttemptWithManualDocuments(adoptionAttempt)
  const isSuspendedAttempt =
    adoptionAttempt?.status === AdoptionAttemptDtoStatus.SuspendedByCustomer ||
    adoptionAttempt?.status === AdoptionAttemptDtoStatus.SuspendedByUser
  // Guest adoptions never have any document to handle through Petso
  const guestCheckout = adoptionAttempt ? getGuestCheckout(adoptionAttempt) : undefined
  const supportsDocuments = !usesManualDocuments && !guestCheckout
  const shouldShowDocumentsSection = supportsDocuments || usesManualDocuments
  const areAllAdoptionDocumentsValidated = Boolean(
    adoptionAttempt?.certificate?.validated && adoptionAttempt?.contract?.validated
  )
  // The placeholder the backend stores for an adoption without documents is not a file: counting it
  // made the card announce a certificate as "Ajouté" and offer to replace one that never existed.
  const certificateHasFile = hasProvidedDocument(
    adoptionAttempt?.certificate?.original,
    adoptionAttempt?.certificate?.signed
  )
  const contractHasFile = hasProvidedDocument(adoptionAttempt?.contract?.original, adoptionAttempt?.contract?.signed)

  const onGuestLinkRefreshed = (link: GuestAdoptionLinkDto) => {
    if (!adoptionAttempt || !guestCheckout) return
    // The previous token is not usable anymore: keep the attempt in sync so it is never displayed nor saved again
    const newAttempt: AdoptionAttemptWithGuestCheckoutDto = {
      ...adoptionAttempt,
      guestCheckout: {
        ...guestCheckout,
        token: link.token,
        amountInCents: link.amountInCents,
        expiresAt: link.expiresAt,
      },
    }
    setAdoptionAttempt(newAttempt)
    adopter.adoptionAttempts = adopter.adoptionAttempts?.map((a) => (a.id === newAttempt.id ? newAttempt : a))
  }

  if (adoptionAttempt == null || animal == null) return null

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: isMobile ? 'auto' : 'white',
        p: isMobile ? '0px' : '30px 15px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          px: isMobile ? 0 : '15px',
        }}
      >
        {/* Animal card */}
        <Grid container spacing={2} paddingX={0}>
          <Grid item xs={12} md={12} paddingX={0}>
            <OnGoingAdoptionAnimalCard
              animal={animal}
              status={getAdoptionAttemptStatus(adoptionAttempt)}
              backTo={`/suivi-adoptant/${adopter.id}?tab=${queryParams.get('tab')}`}
              onComplete={() => onClickEnd(true)}
              showCompleteAction={!isSuspendedAttempt && !adoptionAttempt.archived}
              completeDisabled={isReader || !CanEditAnimal || updateAttemptMutation.isLoading}
            />
            {!isSuspendedAttempt && (
              <AllAdoptionInsightsInfoBox
                attempt={adoptionAttempt}
                setAdoptionAttempt={setAdoptionAttempt}
                updateAttemptMutation={updateAttemptMutation}
                adopterId={adopter.id}
                disabled={isReader || !CanEditAnimal}
              />
            )}
          </Grid>
          {/* Guest adoption: payment link & payment status */}
          {guestCheckout && (
            <Grid item xs={12} md={12} paddingX={0}>
              <GuestAdoptionCard
                adopterId={adopter.id}
                attemptId={adoptionAttempt.id}
                guestCheckout={guestCheckout}
                onLinkRefreshed={onGuestLinkRefreshed}
                disabled={isReader || !CanEditAnimal}
              />
            </Grid>
          )}
        </Grid>
        {/* If adoption suspension: Buttons: delete, start again */}
        {isSuspendedAttempt && (
          <Box
            sx={{
              ...spacingItem,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              width: '100%',
              px: '16px',
              gap: 1,
            }}
          >
            <LoadingButton
              variant="contained"
              color="error"
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              onClick={() => deleteAttempt()}
              disabled={isReader || !CanEditAnimal}
            >
              Supprimer l&#39;adoption
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={updateAttemptMutation.isLoading}
              disabled={isReader || !CanEditAnimal}
              onClick={() => onClickRestartAttempt()}
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
            >
              Reprendre l&#39;adoption
            </LoadingButton>
          </Box>
        )}
        {/* Adoption documents */}
        {!isSuspendedAttempt && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                mt: 2,
              }}
            >
              {/* Suivi d'adoption */}
              <AdoptionAttemptEvents
                animal={animal}
                attempt={adoptionAttempt}
                editAttemptMutation={updateAttemptMutation}
                disabled={isReader || !CanEditAnimal}
              />
              {/* Certificat & contract */}
              {shouldShowDocumentsSection ? (
                <CollapsableCard defaultExpanded={!areAllAdoptionDocumentsValidated} title="Documents d'adoption">
                  <Grid container spacing={isMobile ? 0 : 2}>
                    {/* Knowledge certificate */}
                    <Grid item xs={12} sx={{ pt: { xs: '16px !important', sm: '16px !important' } }}>
                      {isPastAdoptionOutsidePetso ? (
                        <FileCard
                          title="Certificat d'engagement"
                          statusLabel={certificateHasFile ? 'Reçu - hors Petso' : 'À compléter'}
                          statusColor={certificateHasFile ? '#DDEFE5' : '#F8E9C7'}
                          description={
                            certificateHasFile
                              ? 'Cette adoption a été réalisée hors Petso. Ce certificat a été importé par vos soins.'
                              : 'Cette adoption a été réalisée hors Petso. Ajoutez le certificat pour compléter le dossier.'
                          }
                          adopter={adopter}
                          animal={animal}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          type="certificate"
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          association={association}
                          fileKey={adoptionAttempt?.certificate?.signed || adoptionAttempt?.certificate?.original}
                          manualImportMode
                          manualImportButtonLabel={certificateHasFile ? 'Remplacer' : 'Ajouter'}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : isPetsoManualDocumentFlow ? (
                        <FileCard
                          title="Certificat d'engagement"
                          statusLabel={certificateHasFile ? 'Ajouté' : 'À compléter'}
                          statusColor={certificateHasFile ? '#DDEFE5' : '#F8E9C7'}
                          description={
                            certificateHasFile
                              ? 'Le certificat a été ajouté manuellement dans Petso.'
                              : guestCheckout
                              ? 'Cette adoption se règle en ligne, sans document signé via Petso. Ajoutez le certificat si vous en avez un.'
                              : 'Cette procédure n’utilise pas l’envoi Petso. Ajoutez le certificat pour compléter le dossier.'
                          }
                          adopter={adopter}
                          animal={animal}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          type="certificate"
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          association={association}
                          fileKey={adoptionAttempt?.certificate?.signed || adoptionAttempt?.certificate?.original}
                          manualImportMode
                          manualImportButtonLabel={
                            certificateHasFile ? 'Remplacer le certificat' : 'Ajouter le certificat'
                          }
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : !adoptionAttempt?.certificate?.original && !adoptionAttempt?.certificate?.signed ? (
                        <FileCard
                          title="Certificat d'engagement"
                          statusLabel="En attente"
                          statusColor="#DCEAF8"
                          description={`${adopter.firstName} attend le certificat de connaissance, veuillez le lui envoyer au plus vite.`}
                          adopter={adopter}
                          animal={animal}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          type="certificate"
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          specie={animal.species}
                          association={association}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : !adoptionAttempt?.certificate?.signed ? (
                        <FileCard
                          title="Certificat d'engagement"
                          statusLabel={
                            adoptionAttempt?.certificate?.deliveredByHand ? 'Remis en main propre' : 'En attente'
                          }
                          statusColor={adoptionAttempt?.certificate?.deliveredByHand ? '#EEF2F7' : '#DCEAF8'}
                          type="certificate"
                          originalFileKey={adoptionAttempt.certificate?.original}
                          skipProcedure
                          animal={animal}
                          association={association}
                          description={
                            !adoptionAttempt?.certificate?.deliveredByHand
                              ? `Le certificat signé vous sera transmis via Petso. Validez sa réception dès qu’il est disponible.`
                              : `Le certificat a été remis hors Petso. Enregistrez-le dès réception pour garder un suivi complet.`
                          }
                          adoptionAttemptId={adoptionAttempt.id}
                          adopter={adopter}
                          setAdoptionAttempt={setAdoptionAttempt}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : !adoptionAttempt?.certificate?.validated ? (
                        <FileCard
                          title="Certificat d'engagement"
                          statusLabel="À valider"
                          statusColor="#F8E9C7"
                          description={`Le certificat a été signé, consultez-le pour validation.`}
                          fileKey={adoptionAttempt.certificate?.signed}
                          originalFileKey={adoptionAttempt.certificate?.original}
                          type="certificate"
                          validation
                          adopter={adopter}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          updateAttemptMutation={updateAttemptMutation}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : (
                        <ExitDocumentCard
                          title="Certificat d'engagement"
                          statusLabel={adoptionAttempt?.certificate?.deliveredByHand ? 'Remis en main propre' : 'Signé'}
                          statusColor={adoptionAttempt?.certificate?.deliveredByHand ? '#EEF2F7' : '#DDEFE5'}
                          type={'knowledge-certificate'}
                          adopterId={adopter.id}
                          adoptionAttemptId={adoptionAttempt.id}
                          originalFileKey={adoptionAttempt.certificate?.signed || ''}
                          initialFileKey={adoptionAttempt.certificate?.original}
                          disabled={isReader || !CanEditAnimal}
                        />
                      )}
                    </Grid>
                    {/* Adoption contract */}
                    <Grid item xs={12} sx={{ pt: { xs: '12px !important', sm: '12px !important' } }}>
                      {isPastAdoptionOutsidePetso ? (
                        <FileCard
                          title="Contrat d'adoption"
                          statusLabel={contractHasFile ? 'Reçu - hors Petso' : 'À compléter'}
                          statusColor={contractHasFile ? '#DDEFE5' : '#F8E9C7'}
                          description={
                            contractHasFile
                              ? 'Cette adoption a été réalisée hors Petso. Ce contrat a été importé par vos soins.'
                              : 'Cette adoption a été réalisée hors Petso. Ajoutez le contrat pour compléter le dossier.'
                          }
                          adopter={adopter}
                          animal={animal}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          type="contract"
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          association={association}
                          fileKey={adoptionAttempt?.contract?.signed || adoptionAttempt?.contract?.original}
                          manualImportMode
                          manualImportButtonLabel={contractHasFile ? 'Remplacer' : 'Ajouter'}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : isPetsoManualDocumentFlow ? (
                        <FileCard
                          title="Contrat d'adoption"
                          statusLabel={contractHasFile ? 'Ajouté' : 'À compléter'}
                          statusColor={contractHasFile ? '#DDEFE5' : '#F8E9C7'}
                          description={
                            contractHasFile
                              ? 'Le contrat a été ajouté manuellement dans Petso.'
                              : guestCheckout
                              ? 'Cette adoption se règle en ligne, sans document signé via Petso. Ajoutez le contrat si vous en avez un.'
                              : 'Cette procédure n’utilise pas l’envoi Petso. Ajoutez le contrat pour compléter le dossier.'
                          }
                          adopter={adopter}
                          animal={animal}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          type="contract"
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          association={association}
                          fileKey={adoptionAttempt?.contract?.signed || adoptionAttempt?.contract?.original}
                          manualImportMode
                          manualImportButtonLabel={contractHasFile ? 'Remplacer le contrat' : 'Ajouter le contrat'}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : !adoptionAttempt?.contract?.original ? (
                        <FileCard
                          title="Contrat d'adoption"
                          statusLabel="En attente"
                          statusColor="#DCEAF8"
                          description={`${adopter.firstName} attend le contrat d'adoption, veuillez le lui envoyer au plus vite.`}
                          adopter={adopter}
                          animal={animal}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          type="contract"
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          association={association}
                          isLocked={adoptionAttempt?.contract?.isLocked}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : !adoptionAttempt?.contract?.signed ? (
                        <FileCard
                          title="Contrat d'adoption"
                          statusLabel={
                            adoptionAttempt?.contract?.deliveredByHand
                              ? 'Remis en main propre'
                              : adoptionAttempt?.contract?.isLocked
                              ? 'Prêt pour envoi'
                              : 'En attente'
                          }
                          statusColor={adoptionAttempt?.contract?.deliveredByHand ? '#EEF2F7' : '#DCEAF8'}
                          type="contract"
                          originalFileKey={adoptionAttempt.contract?.original}
                          description={
                            adoptionAttempt?.contract?.isLocked
                              ? "Le contrat a été généré et est prêt pour l'envoi."
                              : !adoptionAttempt?.contract?.deliveredByHand
                              ? `Le contrat signé vous sera transmis via Petso. Validez sa réception dès qu’il est disponible.`
                              : `Le contrat a été remis hors Petso. Enregistrez-le dès réception pour garder un suivi complet.`
                          }
                          skipProcedure
                          animal={animal}
                          association={association}
                          adoptionAttemptId={adoptionAttempt.id}
                          adopter={adopter}
                          setAdoptionAttempt={setAdoptionAttempt}
                          updateAttemptMutation={updateAttemptMutation}
                          adoptionAttempt={adoptionAttempt}
                          isLocked={adoptionAttempt?.contract?.isLocked}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : !adoptionAttempt?.contract?.validated ? (
                        <FileCard
                          title="Contrat d'adoption"
                          statusLabel="À valider"
                          statusColor="#F8E9C7"
                          description={`Le contrat a été signé, consultez-le pour validation.`}
                          fileKey={adoptionAttempt.contract?.signed}
                          originalFileKey={adoptionAttempt.contract?.original}
                          type="contract"
                          validation
                          adopter={adopter}
                          adoptionAttemptId={adoptionAttempt.id}
                          adoptionAttempt={adoptionAttempt}
                          setAdoptionAttempt={setAdoptionAttempt}
                          isReader={isReader}
                          updateAttemptMutation={updateAttemptMutation}
                          disabled={isReader || !CanEditAnimal}
                        />
                      ) : (
                        <ExitDocumentCard
                          title="Contrat d'adoption"
                          statusLabel={adoptionAttempt?.contract?.deliveredByHand ? 'Remis en main propre' : 'Signé'}
                          statusColor={adoptionAttempt?.contract?.deliveredByHand ? '#EEF2F7' : '#DDEFE5'}
                          type={'adoption-contract'}
                          adopterId={adopter.id}
                          adoptionAttemptId={adoptionAttempt.id}
                          originalFileKey={adoptionAttempt.contract?.signed || ''}
                          initialFileKey={adoptionAttempt.contract?.original}
                          disabled={isReader || !CanEditAnimal}
                        />
                      )}
                    </Grid>
                  </Grid>
                </CollapsableCard>
              ) : (
                <Card sx={{ width: '100%' }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h4" component="p" sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700 }}>
                      Documents d&#39;adoption
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      ⁠Vous n&apos;avez pas transmis les documents via Petso.
                    </Typography>
                  </Box>
                </Card>
              )}
              <CollapsableCard title="Note interne" defaultExpanded={false}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Commentaire"
                      fullWidth
                      multiline
                      rows={4}
                      value={adoptionAttempt?.comment || ''}
                      onChange={(e) => {
                        const newAttempt = {
                          ...adoptionAttempt,
                          comment: e.target.value,
                        } as AdoptionAttemptDto
                        setAdoptionAttempt(newAttempt)
                      }}
                      disabled={isReader || !CanEditAnimal}
                      size="small"
                      sx={spacingItem}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton
                      variant="outlined"
                      onClick={() => {
                        if (adoptionAttempt) {
                          updateAttemptMutation.mutate(adoptionAttempt)
                        }
                      }}
                      loading={updateAttemptMutation.isLoading}
                      disabled={isReader || !CanEditAnimal}
                      sx={addSecondaryFabSx}
                    >
                      Enregistrer
                    </LoadingButton>
                  </Grid>
                </Grid>
              </CollapsableCard>
              <ContributionCard
                adoptionAttempt={adoptionAttempt}
                updateAttemptMutation={updateAttemptMutation}
                animal={animal}
                isReader={isReader || !CanEditAnimal}
                adopterId={adopter.id}
                setAdoptionAttempt={setAdoptionAttempt}
                defaultOpen={!supportsDocuments}
              />
              <SterilizationCard
                adoptionAttempt={adoptionAttempt}
                adopter={adopter}
                updateAttemptMutation={updateAttemptMutation}
                setAdoptionAttempt={setAdoptionAttempt}
                animal={animal}
                isReader={isReader || !CanEditAnimal}
              />
              <ExitDatesCard
                animal={animal}
                adopter={adopter}
                defaultExpanded={getAdoptionAttemptStatus(adoptionAttempt) === 'Adoption à finaliser'}
                endProcedure={onClickEnd}
              />
              <ExitDocumentsCard
                adoptionAttempt={adoptionAttempt}
                adopterId={adopter.id}
                defaultExpanded={getAdoptionAttemptStatus(adoptionAttempt) === 'Adoption à finaliser'}
                isReader={isReader || !CanEditAnimal}
              />
              {isMobile ? (
                // MOBILE
                <Card
                  sx={{
                    width: '100%',
                    mb: '100px',
                    p: '16px',
                  }}
                >
                  <Box display="flex" flexDirection="column" gap={1}>
                    {!adoptionAttempt.archived && (
                      <LoadingButton
                        variant="contained"
                        color="secondary"
                        sx={[addPrimaryFabSx, { width: '100%' }]}
                        loading={updateAttemptMutation.isLoading}
                        onClick={() => onClickEnd(true)}
                        disabled={isReader || !CanEditAnimal}
                      >
                        Marquer comme terminée
                      </LoadingButton>
                    )}

                    <Button
                      variant="text"
                      sx={[
                        {
                          width: '100%',
                          color: '#B35558',
                          backgroundColor: 'transparent',
                          borderColor: 'transparent',
                          boxShadow: 'none',
                          mt: 1,
                          pt: 1.25,
                          pb: 1,
                          textTransform: 'none',
                          fontSize: '14px',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'rgba(179, 85, 88, 0.06)',
                            borderColor: 'transparent',
                          },
                        },
                      ]}
                      onClick={() => setSuspendModalOpen(true)}
                      disabled={isReader || !CanEditAnimal}
                    >
                      Suspendre l&#39;adoption
                    </Button>
                  </Box>
                </Card>
              ) : (
                // DESKTOP
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    width: '100%',
                    mt: 2,
                  }}
                >
                  <Button
                    variant="text"
                    sx={[
                      {
                        fontSize: '14px',
                        minHeight: 40,
                        height: 40,
                        color: '#B35558',
                        backgroundColor: 'transparent',
                        borderColor: 'transparent',
                        boxShadow: 'none',
                        mt: 1,
                        pt: 1.25,
                        pb: 1,
                        px: 0.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: 'rgba(179, 85, 88, 0.06)',
                          borderColor: 'transparent',
                        },
                      },
                    ]}
                    onClick={() => setSuspendModalOpen(true)}
                    disabled={isReader || !CanEditAnimal}
                  >
                    Suspendre l&#39;adoption
                  </Button>
                  {!adoptionAttempt.archived && (
                    <LoadingButton
                      variant="contained"
                      color="secondary"
                      sx={[addPrimaryFabSx, { fontSize: '15px', minHeight: 40, height: 40 }]}
                      loading={updateAttemptMutation.isLoading}
                      onClick={() => onClickEnd(true)}
                      disabled={isReader || !CanEditAnimal}
                    >
                      Marquer comme terminée
                    </LoadingButton>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
      <RestartProcedureModal onClose={onCloseRestartModal} isOpen={restartModalOpen} onSubmit={onClickRestartAttempt} />
      <SuspendProcedureModal
        onClose={onCloseSuspendModal}
        isOpen={suspendModalOpen}
        onSubmit={onClickSuspendAttempt}
        animalName={animal.name}
        isLoading={updateAttemptMutation.isLoading}
      />
      <UnlockContractModal
        onClose={onCloseUnlockModal}
        isOpen={unlockModalOpen}
        onSubmit={onClickUnlockContract}
        isLoading={updateAttemptMutation.isLoading}
      />
      <EndAdoptionPopUp
        open={endModalOpen}
        setOpen={setEndModalOpen}
        adopterId={adopter.id}
        adopterEmail={adopter.email}
        animal={animal}
        attempt={adoptionAttempt}
        updateAttemptMutation={updateAttemptMutation}
        setAdoptionAttempt={setAdoptionAttempt}
        setAnimal={setAnimal}
      />
    </Box>
  )
}
